// Numbered steps down a thin rail. A step is `done` (a green check),
// `current` (the one to do next), `failed`, or `todo`. `summary` is the
// step's current value in one line; `action` sits at the title's end;
// `collapsed` shows only the number and title until the step can be used.
const BADGE = {
    done: "border border-[color:var(--green)] text-[color:var(--green)]",
    current: "bg-[color:var(--ink)] text-[color:var(--bg)]",
    failed: "bg-[color:var(--danger)] text-[color:var(--bg)]",
    todo: "border border-[color:var(--line-strong)] text-[color:var(--muted)]",
};

const STATE_TEXT = { done: "Done", current: "Next step", failed: "Needs attention", todo: "Not started" };

export function Steps({ label, className = "", children }) {
    return <ol aria-label={label} className={`m-0 grid list-none gap-0 p-0 ${className}`}>{children}</ol>;
}

export function Step({ number, state = "todo", title, summary = null, action = null, collapsed = false, last = false, children, ...attrs }) {
    return (
        <li aria-current={state === "current" ? "step" : undefined} className="grid grid-cols-[26px_minmax(0,1fr)] gap-x-[14px]" {...attrs}>
            <div className="flex flex-col items-center">
                <span className={`inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-[12px] font-semibold ${BADGE[state] ?? BADGE.todo}`}>
                    {state === "done" ? (
                        <svg aria-hidden="true" viewBox="0 0 12 12" className="h-[12px] w-[12px]">
                            <path d="M2.5 6.5 5 9l4.5-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ) : (
                        <span aria-hidden="true">{state === "failed" ? "!" : number}</span>
                    )}
                    <span className="sr-only">{`Step ${number}: ${STATE_TEXT[state] ?? ""}`}</span>
                </span>
                {last ? null : <span aria-hidden="true" className="mt-[6px] w-px flex-1 bg-[color:var(--line)]" />}
            </div>
            <div className={`grid min-w-0 content-start gap-[12px] pt-[3px] ${last ? "" : "pb-[26px]"}`}>
                <div className="flex flex-wrap items-start justify-between gap-[10px]">
                    <div className="grid min-w-0 gap-[3px]">
                        <h3 className={`m-0 text-[14px] font-medium leading-[1.4] ${collapsed ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}>{title}</h3>
                        {summary ? (
                            <div className={`text-[12.5px] leading-[1.4] ${state === "failed" ? "text-[color:var(--danger)]" : "text-[color:var(--muted)]"}`}>{summary}</div>
                        ) : null}
                    </div>
                    {action ? <div className="flex items-center gap-[8px]">{action}</div> : null}
                </div>
                {collapsed ? null : children}
            </div>
        </li>
    );
}
